"use client";

import React, { useState } from "react";
import {
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Tooltip,
} from "@chakra-ui/react";
import { Settings } from "lucide-react";
import { useAppTheme } from "../../../ThemeContext.tsx";
import ReactSelect from "react-select";

interface BackgroundSoundSelectProps {
  value: string | null | undefined;
  onChange: (value: string | null) => Promise<boolean> | void;
}

const backgroundSoundOptions = [
  { value: null, label: "None" },
  { value: "coffee-shop", label: "Coffee Shop" },
  { value: "convention-hall", label: "Convention Hall" },
  { value: "summer-outdoor", label: "Summer Outdoor" },
  { value: "mountain-outdoor", label: "Mountain Outdoor" },
  { value: "static-noise", label: "Static Noise" },
  { value: "call-center", label: "Call Center" },
];

export const BackgroundSoundSelect: React.FC<BackgroundSoundSelectProps> = ({
  value,
  onChange,
}) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  // Convert options to react-select format.
  const options = backgroundSoundOptions.map((option) => ({
    value: option.value === null ? "null" : option.value,
    label: option.label,
  }));

  // Find the current selection.
  const selectedOption =
    options.find(
      (option) => option.value === (value === null ? "null" : value)
    ) || null;

  const handleChange = async (option: any) => {
    const newValue = option
      ? option.value === "null"
        ? null
        : option.value
      : null;
    setIsUpdating(true);
    try {
      await onChange(newValue);
    } finally {
      setIsUpdating(false);
    }
  };

  const customStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: isDark ? "gray.700" : "white",
      borderColor: isDark ? "gray.600" : "gray.300",
      minHeight: "28px",
      fontSize: "12px",
      // Prevent the gray border by removing the focus box shadow
      boxShadow: "none",
      outline: "none",
      "&:hover": {
        borderColor: isDark ? "gray.500" : "gray.400",
      },
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isFocused ? "gray" : isDark ? "#2D3748" : "white",
      color: isDark ? "white" : "black",
      fontSize: "12px",
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: isDark ? "white" : "black",
      fontSize: "12px",
    }),
    menu: (provided: any) => ({
      ...provided,
      backgroundColor: isDark ? "#2D3748" : "white",
      zIndex: 9999, // Ensures the dropdown menu appears above other elements
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      padding: 4,
    }),
  };

  return (
    <FormControl>
      <HStack justify="space-between" align="flex-start" mb={2}>
        <FormLabel
          fontSize="sm"
          fontWeight="medium"
          color={isDark ? "gray.300" : "gray.700"}
          mb={0}
        >
          Background Sound
        </FormLabel>
        <Tooltip label="Advanced settings" placement="top">
          <IconButton
            aria-label="Advanced settings"
            icon={<Settings size={16} />}
            size="xs"
            variant="ghost"
            visibility="hidden"
          />
        </Tooltip>
      </HStack>
      <ReactSelect
        value={selectedOption}
        onChange={handleChange}
        options={options}
        styles={customStyles}
        isDisabled={isUpdating}
        isSearchable={false}
      />
    </FormControl>
  );
};
